// What an investigation made of the cases a workspace was opened for, for its
// walkthrough to read (lib/investigateAgent.js narrateSession and
// narrateWorkspace): each case's title and account, what its dig concluded,
// the sessions it found related and why, and its notes (Case note 1, Case
// note 2, ..., numbered across the cases), each on the message it names where
// it names one. They are leads, as the review's findings and the chat's
// claims are: the walkthrough tells the story from the records.
//
// PURE: the cases come as their scope keeps them (lib/investigateWorkspace.js
// boardCases reads a board's).

// Cases one walkthrough is told of, and their notes, at most.
export const WALK_CASES_MAX = 8;
export const WALK_CASE_NOTES_MAX = 24;
const TITLE_CHARS = 140;
const ACCOUNT_CHARS = 600;
const WHY_CHARS = 300;
const NOTE_CHARS = 400;
const QUOTE_CHARS = 200;
const RELATED_MAX = 4;
// What a note says of its moment, as the case's notes are kinded.
const KIND_WORDS = { matters: "matters", doesnt: "does not matter", missed: "missed by the review", corrects: "corrects the review's check" };
const VERDICT_WORDS = { yes: "that a person should follow it up", no: "that it can be let go", unsure: "nothing either way from what it read" };

const str = (v) => (v == null ? "" : String(v));
const lower = (v) => str(v).trim().toLowerCase();
const clip = (v, n) => {
    const s = str(v).replace(/\s+/g, " ").trim();
    return s.length > n ? `${s.slice(0, n - 3)}...` : s;
};

/**
 * What the walkthrough is told of `cases` (as lib/investigateStore.js keeps
 * them): [{ ref, id, title, account, verdict: { needs, why } | null,
 * related: [{ session, why }], notes: [{ n, ref, id, kind, text, by,
 * session, messageId, quote }] }], the notes numbered across the cases as the
 * walkthrough names them.
 */
export function walkCases(cases) {
    let n = 0;
    return (Array.isArray(cases) ? cases : []).filter((c) => c?.id).slice(0, WALK_CASES_MAX).map((c, i) => ({
        ref: `Case ${i + 1}`,
        id: c.id,
        title: clip(c.title, TITLE_CHARS),
        account: clip(c.account, ACCOUNT_CHARS),
        verdict: c.dig?.status === "done" && c.dig.needsPerson ? { needs: c.dig.needsPerson, why: clip(c.dig.why, WHY_CHARS) } : null,
        related: (c.related || []).slice(0, RELATED_MAX).map((r) => ({ session: lower(r.session), why: clip(r.why, WHY_CHARS) })),
        notes: (c.notes || []).filter((x) => str(x?.text).trim()).map((x) => {
            if (n >= WALK_CASE_NOTES_MAX) return null;
            n += 1;
            return {
                n, ref: `Case note ${n}`, id: str(x.id), kind: x.kind || "note", text: clip(x.text, NOTE_CHARS), by: x.by === "you" ? "you" : "investigate",
                session: x.session ? lower(x.session) : null, messageId: x.messageId ? str(x.messageId).trim() : null, quote: x.quote ? clip(x.quote, QUOTE_CHARS) : null,
            };
        }).filter(Boolean),
    }));
}

// The messages of `session` the cases' notes name, for its record to be read
// around them as around the review's citations.
const CASE_MESSAGES_MAX = 12;
export function caseMessages(cases, session) {
    const s = lower(session);
    const ids = (cases || []).flatMap((c) => (c.notes || []).filter((x) => x.session === s && x.messageId).map((x) => x.messageId));
    return [...new Set(ids)].slice(0, CASE_MESSAGES_MAX);
}

// Where a note or a related session is, in words: a session by its ref on
// the workspace (`refOf(id)`), and the message a note names.
function placeWords(session, messageId, refOf) {
    if (!session) return "";
    const ref = refOf(session);
    if (!ref) return "a session not on the workspace";
    return messageId ? `${ref} message [${messageId}]` : ref;
}

/** The brief's lines for the cases: none where there are none. */
export function casesBrief(cases, { refOf = () => null } = {}) {
    if (!cases?.length) return [];
    const lines = ["", `What an investigation made of the case${cases.length === 1 ? "" : "s"} this workspace was opened for, named as the task names them (leads to check against the records, not the story):`];
    for (const c of cases) {
        lines.push(`${c.ref}${c.title ? ` - "${c.title}"` : ""}`);
        if (c.account) lines.push(`  Its account: ${c.account}`);
        if (c.verdict) lines.push(`  Its dig concluded ${VERDICT_WORDS[c.verdict.needs] || "nothing"}${c.verdict.why ? `: ${c.verdict.why}` : "."}`);
        for (const r of c.related) {
            const where = placeWords(r.session, null, refOf);
            if (where) lines.push(`  Related: ${where}${r.why ? ` - ${r.why}` : ""}`);
        }
        for (const x of c.notes) {
            const where = placeWords(x.session, x.messageId, refOf);
            const kind = KIND_WORDS[x.kind] ? ` - ${KIND_WORDS[x.kind]}` : "";
            lines.push(`  ${x.ref}${x.by === "you" ? " (the reader's own)" : ""}${kind}${where ? ` - ${where}` : ""} - ${x.text}${x.quote ? ` (quoting "${x.quote}")` : ""}`);
        }
    }
    return lines;
}
